/**
 * SIM-1502 — inline editor for universal (T1) field definitions. Mirrors the structured form from
 * FieldDefinitionsSection: description, comma-separated aliases, per-table override map. Unknown
 * body fields on the existing row are preserved on update, so future schema additions survive.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  Input,
  Label,
  QueryView,
  Textarea,
} from "@simplicity/ui";
import { useState } from "react";
import { notifyMutationError, notifyWarnings } from "./notify";
import type { AgentFieldDefinition, FieldBody } from "./types";
import {
  useCreateFieldDef,
  useDeleteFieldDef,
  useFieldDefs,
  useUpdateFieldDef,
} from "./useDottieContext";

interface PerTableEntry {
  table: string;
  description: string;
}

interface FormState {
  fieldName: string;
  description: string;
  aliasesText: string;
  perTable: PerTableEntry[];
  reason: string;
}

const EMPTY_FORM: FormState = {
  fieldName: "",
  description: "",
  aliasesText: "",
  perTable: [],
  reason: "",
};

function asString(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

function asPerTable(v: unknown): PerTableEntry[] {
  if (!v || typeof v !== "object" || Array.isArray(v)) return [];
  const out: PerTableEntry[] = [];
  for (const [table, description] of Object.entries(v as Record<string, unknown>)) {
    if (typeof description === "string") out.push({ table, description });
  }
  return out;
}

function fromFieldDef(row: AgentFieldDefinition): FormState {
  const body = row.body;
  return {
    fieldName: row.fieldName,
    description: asString(body.description),
    aliasesText: asStringArray(body.aliases).join(", "),
    perTable: asPerTable(body.perTable),
    reason: "",
  };
}

function buildBody(form: FormState, existing: FieldBody | undefined): FieldBody {
  const aliases = form.aliasesText
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const perTable: Record<string, string> = {};
  for (const e of form.perTable) {
    if (e.table.trim()) perTable[e.table.trim()] = e.description;
  }
  return {
    ...(existing ?? {}),
    description: form.description,
    aliases,
    perTable,
  };
}

function PerTableEditor({
  rows,
  onChange,
}: {
  rows: PerTableEntry[];
  onChange: (next: PerTableEntry[]) => void;
}) {
  return (
    <div className="space-y-2">
      {rows.map((row, i) => (
        <div key={`pt-${i}`} className="flex gap-2">
          <Input
            aria-label={`Per-table table name ${i + 1}`}
            placeholder="table_name"
            value={row.table}
            onChange={(e) => {
              const next = [...rows];
              next[i] = { ...row, table: e.target.value };
              onChange(next);
            }}
          />
          <Input
            aria-label={`Per-table description ${i + 1}`}
            placeholder="Override description"
            value={row.description}
            onChange={(e) => {
              const next = [...rows];
              next[i] = { ...row, description: e.target.value };
              onChange(next);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
            aria-label={`Remove per-table override ${i + 1}`}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...rows, { table: "", description: "" }])}
      >
        Add per-table override
      </Button>
    </div>
  );
}

function FieldDefInlineForm({
  initial,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
  idPrefix,
}: {
  initial: FormState;
  submitLabel: string;
  pending: boolean;
  onSubmit: (form: FormState) => void;
  onCancel: () => void;
  idPrefix: string;
}) {
  const [form, setForm] = useState<FormState>(initial);
  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-name`}>Field name</Label>
        <Input
          id={`${idPrefix}-name`}
          value={form.fieldName}
          onChange={(e) => setForm({ ...form, fieldName: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-description`}>Description</Label>
        <Textarea
          id={`${idPrefix}-description`}
          rows={4}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-aliases`}>Aliases (comma-separated)</Label>
        <Input
          id={`${idPrefix}-aliases`}
          value={form.aliasesText}
          onChange={(e) => setForm({ ...form, aliasesText: e.target.value })}
          placeholder="grade, grade_level, level"
        />
      </div>
      <div className="space-y-1">
        <div className="text-sm font-medium leading-none">Per-table overrides</div>
        <PerTableEditor
          rows={form.perTable}
          onChange={(perTable) => setForm({ ...form, perTable })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-reason`}>Reason (optional)</Label>
        <Input
          id={`${idPrefix}-reason`}
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => onSubmit(form)}
          disabled={pending || form.fieldName.trim() === ""}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function FieldDefRow({
  row,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onRequestDelete,
  onSave,
  pending,
}: {
  row: AgentFieldDefinition;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onRequestDelete: () => void;
  onSave: (form: FormState) => void;
  pending: boolean;
}) {
  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="font-medium text-foreground">{row.fieldName}</div>
          <div className="text-sm text-muted-foreground">{asString(row.body.description)}</div>
        </div>
        <div className="flex shrink-0 gap-2">
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={onStartEdit}>
              Edit
            </Button>
          )}
          <Button variant="destructive" size="sm" onClick={onRequestDelete}>
            Delete
          </Button>
        </div>
      </div>
      {isEditing && (
        <div className="mt-3">
          <FieldDefInlineForm
            initial={fromFieldDef(row)}
            submitLabel="Save"
            pending={pending}
            onSubmit={onSave}
            onCancel={onCancelEdit}
            idPrefix={`u-fd-edit-${row.id}`}
          />
        </div>
      )}
    </li>
  );
}

function useUniversalFieldDefMutations(
  deleteTarget: AgentFieldDefinition | null,
  setCreating: (v: boolean) => void,
  setEditingId: (v: string | null) => void,
  setDeleteTarget: (v: AgentFieldDefinition | null) => void,
) {
  const createFieldDef = useCreateFieldDef(null);
  const updateFieldDef = useUpdateFieldDef(null);
  const deleteFieldDef = useDeleteFieldDef(null);

  const handleCreate = (form: FormState) =>
    createFieldDef.mutate(
      {
        tenantId: null,
        fieldName: form.fieldName,
        body: buildBody(form, undefined),
        reason: form.reason || undefined,
      },
      {
        onSuccess: (res) => {
          notifyWarnings(res.warnings);
          setCreating(false);
        },
        onError: (err) => notifyMutationError("Create universal field definition", err),
      },
    );

  const handleUpdate = (row: AgentFieldDefinition, form: FormState) =>
    updateFieldDef.mutate(
      {
        id: row.id,
        fieldName: form.fieldName,
        body: buildBody(form, row.body),
        reason: form.reason || undefined,
      },
      {
        onSuccess: (res) => {
          notifyWarnings(res.warnings);
          setEditingId(null);
        },
        onError: (err) => notifyMutationError("Update universal field definition", err),
      },
    );

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteFieldDef.mutate(
      { id: deleteTarget.id },
      {
        onSuccess: () => setDeleteTarget(null),
        onError: (err) => notifyMutationError("Delete universal field definition", err),
      },
    );
  };

  return { createFieldDef, updateFieldDef, handleCreate, handleUpdate, handleDelete };
}

export function UniversalFieldDefsEditor() {
  const fieldDefsQuery = useFieldDefs(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AgentFieldDefinition | null>(null);

  const { createFieldDef, updateFieldDef, handleCreate, handleUpdate, handleDelete } =
    useUniversalFieldDefMutations(deleteTarget, setCreating, setEditingId, setDeleteTarget);

  return (
    <section className="space-y-3" aria-labelledby="universal-field-defs-heading">
      <div className="flex items-center justify-between">
        <h3 id="universal-field-defs-heading" className="text-lg font-semibold text-foreground">
          Universal field definitions
        </h3>
        {!creating && (
          <Button size="sm" onClick={() => setCreating(true)}>
            Add universal field
          </Button>
        )}
      </div>

      {creating && (
        <FieldDefInlineForm
          initial={EMPTY_FORM}
          submitLabel="Create"
          pending={createFieldDef.isPending}
          onSubmit={handleCreate}
          onCancel={() => setCreating(false)}
          idPrefix="u-fd-new"
        />
      )}

      <QueryView
        query={fieldDefsQuery}
        error={(err) => (
          <p className="text-sm text-destructive" role="alert">
            Failed to load universal field definitions: {err.message}
          </p>
        )}
      >
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState
              title="No universal field definitions"
              description="Add a field definition that every tenant inherits."
            />
          ) : (
            <ul className="space-y-2">
              {rows.map((row) => (
                <FieldDefRow
                  key={row.id}
                  row={row}
                  isEditing={editingId === row.id}
                  onStartEdit={() => setEditingId(row.id)}
                  onCancelEdit={() => setEditingId(null)}
                  onRequestDelete={() => setDeleteTarget(row)}
                  onSave={(form) => handleUpdate(row, form)}
                  pending={updateFieldDef.isPending}
                />
              ))}
            </ul>
          )
        }
      </QueryView>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete universal field definition?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the field definition for every tenant. A revision is recorded so the
              prior content stays viewable in History.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
